import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useUser } from "@clerk/react";
import { useTranslation } from "react-i18next";
import { listPools, type Pool } from "@workspace/api-client-react";
import { Heart, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import DonateModal from "@/components/DonateModal";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { useState, useEffect, useMemo } from "react";

const SAGE = "#A8B5A2";
const TERRACOTTA = "#D9B8A3";
const CLAY = "#E8DAB2";

const POOL_CATEGORIES = [
  { key: "all", label: "All" },
  { key: "medical", label: "Medical" },
  { key: "credit_card", label: "Credit Card" },
  { key: "student", label: "Student Loan" },
  { key: "small_business", label: "Small Business" },
  { key: "housing", label: "Housing" },
  { key: "auto", label: "Auto" },
  { key: "collections", label: "Collections" },
  { key: "veterans", label: "Veterans" },
  { key: "legal", label: "Legal" },
  { key: "personal_loan", label: "Personal Loan" },
  { key: "family", label: "Family" },
];

function PoolCard({ pool, onDonate }: { pool: Pool; onDonate: (pool: Pool) => void }) {
  const { t } = useTranslation();
  return (
    <Card
      style={{
        borderRadius: "16px",
        border: "1px solid rgba(0,0,0,0.07)",
        overflow: "hidden",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        cursor: "default",
        background: "rgba(255,255,255,0.85)",
        backdropFilter: "blur(8px)",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = "translateY(-2px)";
        (e.currentTarget as HTMLDivElement).style.boxShadow = "0 12px 32px rgba(0,0,0,0.08)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
        (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
      }}
    >
      <CardHeader style={{ paddingBottom: "10px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
          <CardTitle style={{ fontSize: "15px", fontWeight: 400, letterSpacing: "-0.01em", lineHeight: 1.35 }}>
            {pool.name}
          </CardTitle>
          <Badge variant={pool.status === "active" ? "default" : "secondary"} className="capitalize shrink-0" style={{ fontSize: "10px" }}>
            {pool.status}
          </Badge>
        </div>
        {pool.category && (
          <span style={{ fontSize: "11px", color: TERRACOTTA, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 300 }}>
            {pool.category.replace(/_/g, " ")}
          </span>
        )}
      </CardHeader>
      <CardContent>
        <Progress value={pool.percentFunded} className="h-1.5 mb-2" style={{ borderRadius: "4px" }} />
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
          <span className="text-muted-foreground" style={{ fontWeight: 300, fontSize: "12px" }}>{pool.percentFunded}% {t("poolsSection.funded")}</span>
          <span style={{ fontSize: "12px", fontWeight: 300, color: "#0a0a0a" }}>
            ${pool.balance.toLocaleString()} raised
          </span>
        </div>
        <button
          onClick={() => onDonate(pool)}
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "10px",
            border: `1.5px solid ${SAGE}`,
            background: "transparent",
            color: "#3a4a37",
            fontSize: "13px",
            fontWeight: 400,
            cursor: pool.status !== "active" ? "not-allowed" : "pointer",
            opacity: pool.status !== "active" ? 0.4 : 1,
            transition: "all 0.2s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
          }}
          disabled={pool.status !== "active"}
          onMouseEnter={(e) => {
            if (pool.status === "active") {
              (e.currentTarget as HTMLButtonElement).style.background = `${SAGE}18`;
            }
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "transparent";
          }}
        >
          <Heart size={13} />
          {t("poolsSection.donateTo")}
        </button>

        {pool.creditorName && (
          <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid rgba(0,0,0,0.06)", display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: "11px", color: SAGE, fontWeight: 300 }}>{pool.creditorName}</span>
            <span style={{ fontSize: "11px", color: "hsl(var(--muted-foreground))", fontWeight: 300 }}>
              ${(pool.threshold - pool.balance).toLocaleString()} {t("poolsSection.remaining")}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PoolsPage() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { isSignedIn } = useUser();
  const [donatePool, setDonatePool] = useState<Pool | null>(null);
  const [poolSearch, setPoolSearch] = useState("");
  const [poolCategory, setPoolCategory] = useState("all");
  const [poolPage, setPoolPage] = useState(1);
  const POOLS_PER_PAGE = 12;

  useEffect(() => {
    setPoolPage(1);
  }, [poolSearch, poolCategory]);

  const poolQueryParams = useMemo(() => ({
    search: poolSearch.trim() || undefined,
    category: poolCategory !== "all" ? poolCategory : undefined,
    page: poolPage,
    limit: POOLS_PER_PAGE,
  }), [poolSearch, poolCategory, poolPage]);

  const { data: poolListData, isLoading: poolsLoading } = useQuery({
    queryKey: ["pools", poolQueryParams],
    queryFn: () => listPools(poolQueryParams),
    placeholderData: (prev) => prev,
  });

  const paginatedPools = poolListData?.items ?? [];
  const totalPoolPages = poolListData?.totalPages ?? 1;
  const filteredCount = poolListData?.totalCount ?? 0;

  return (
    <div style={{ minHeight: "100vh", background: "hsl(var(--background))", fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
      <SiteNav />

      <section style={{ position: "relative", padding: "80px 20px 100px", overflow: "hidden" }}>
        {/* Charitable/community — giving hands, ghost texture */}
        <img
          src="https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=1600&q=80&fit=crop&auto=format"
          alt="" aria-hidden loading="lazy"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.13, filter: "grayscale(0.15)", pointerEvents: "none" }}
        />
        <div aria-hidden style={{
          position: "absolute", inset: 0,
          background: `linear-gradient(180deg, rgba(232,218,178,0.07) 0%, rgba(168,181,162,0.04) 100%)`,
          pointerEvents: "none",
        }} />

        <div style={{ position: "relative", maxWidth: "1200px", margin: "0 auto" }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "36px", flexWrap: "wrap", gap: "16px" }}>
            <div>
              <p style={{ fontSize: "11px", fontWeight: 400, letterSpacing: "0.12em", textTransform: "uppercase", color: CLAY, marginBottom: "8px" }}>
                {t("poolsSection.eyebrow")}
              </p>
              <h1 style={{ fontSize: "clamp(26px, 4vw, 44px)", fontWeight: 200, letterSpacing: "-0.03em", marginBottom: "6px" }}>{t("poolsSection.headline")}</h1>
              <p style={{ color: "hsl(var(--muted-foreground))", fontWeight: 300, fontSize: "14px" }}>{t("poolsSection.subtext")}</p>
            </div>
            {isSignedIn && (
              <button
                style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  padding: "10px 22px", borderRadius: "50px", fontSize: "13px",
                  fontWeight: 300, letterSpacing: "0.04em", cursor: "pointer",
                  background: "#0a0a0a", color: "#ffffff", border: "1px solid #0a0a0a",
                  transition: "all 0.3s ease",
                }}
                onClick={() => navigate("/donor")}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 3px rgba(168,181,162,0.35)`; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "none"; }}
              >
                {t("poolsSection.myDonations")} <ChevronRight size={14} />
              </button>
            )}
          </div>

          {/* Search */}
          <div style={{ marginBottom: "24px" }}>
            <div style={{ position: "relative", maxWidth: "480px" }}>
              <span style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "hsl(var(--muted-foreground))", pointerEvents: "none", lineHeight: 1 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search pools, creditors, or categories…"
                value={poolSearch}
                onChange={(e) => { setPoolSearch(e.target.value); setPoolPage(1); }}
                style={{
                  width: "100%",
                  paddingLeft: "38px",
                  paddingRight: poolSearch ? "36px" : "14px",
                  paddingTop: "10px",
                  paddingBottom: "10px",
                  borderRadius: "50px",
                  border: "1px solid rgba(0,0,0,0.1)",
                  background: "rgba(255,255,255,0.85)",
                  backdropFilter: "blur(8px)",
                  fontSize: "13px",
                  fontWeight: 300,
                  outline: "none",
                  color: "var(--foreground)",
                  boxSizing: "border-box",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                }}
                onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 3px rgba(168,181,162,0.3)`; e.currentTarget.style.borderColor = SAGE; }}
                onBlur={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "rgba(0,0,0,0.1)"; }}
              />
              {poolSearch && (
                <button
                  onClick={() => { setPoolSearch(""); setPoolPage(1); }}
                  style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "hsl(var(--muted-foreground))", padding: "2px", lineHeight: 1 }}
                  aria-label="Clear search"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Category tabs */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "32px", overflowX: "auto", paddingBottom: "4px", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
            {POOL_CATEGORIES.map((cat) => {
              const isActive = poolCategory === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => { setPoolCategory(cat.key); setPoolPage(1); }}
                  style={{
                    whiteSpace: "nowrap",
                    padding: "7px 16px",
                    borderRadius: "50px",
                    border: isActive ? `1.5px solid ${SAGE}` : "1.5px solid rgba(0,0,0,0.09)",
                    background: isActive ? "rgba(168,181,162,0.18)" : "rgba(255,255,255,0.7)",
                    color: isActive ? "#3a4a37" : "hsl(var(--muted-foreground))",
                    fontSize: "12px",
                    fontWeight: isActive ? 500 : 300,
                    letterSpacing: "0.01em",
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* Results count */}
          {poolListData && (
            <p style={{ fontSize: "12px", color: "hsl(var(--muted-foreground))", fontWeight: 300, marginBottom: "20px", letterSpacing: "0.02em" }}>
              {filteredCount === 0
                ? "No pools match your search."
                : `Showing ${Math.min((poolPage - 1) * POOLS_PER_PAGE + 1, filteredCount)}–${Math.min(poolPage * POOLS_PER_PAGE, filteredCount)} of ${filteredCount} pool${filteredCount !== 1 ? "s" : ""}`
              }
            </p>
          )}

          {/* Grid */}
          {poolsLoading && !poolListData ? (
            <div style={{ textAlign: "center", padding: "80px 0", color: "hsl(var(--muted-foreground))" }}>
              <div style={{ width: "32px", height: "32px", margin: "0 auto 16px", border: `2px solid ${SAGE}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              <p style={{ fontWeight: 300, fontSize: "14px" }}>Loading pools…</p>
            </div>
          ) : filteredCount === 0 ? (
            <div style={{ textAlign: "center", padding: "80px 0", color: "hsl(var(--muted-foreground))" }}>
              <Heart style={{ width: "44px", height: "44px", margin: "0 auto 16px", opacity: 0.18 }} />
              <p style={{ fontWeight: 300, fontSize: "15px" }}>
                {poolSearch ? `No pools found for "${poolSearch}"` : t("poolsSection.noPools")}
              </p>
              {(poolSearch || poolCategory !== "all") && (
                <button
                  onClick={() => { setPoolSearch(""); setPoolCategory("all"); setPoolPage(1); }}
                  style={{ marginTop: "12px", fontSize: "12px", color: SAGE, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "20px" }}>
              {paginatedPools.map((pool) => (
                <PoolCard
                  key={pool.id}
                  pool={pool}
                  onDonate={(p) => {
                    if (!isSignedIn) navigate("/sign-up");
                    else setDonatePool(p);
                  }}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPoolPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginTop: "48px", flexWrap: "wrap" }}>
              <button
                onClick={() => { setPoolPage((p) => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                disabled={poolPage === 1}
                style={{ padding: "8px 18px", borderRadius: "50px", border: "1.5px solid rgba(0,0,0,0.1)", background: "rgba(255,255,255,0.8)", fontSize: "12px", fontWeight: 300, cursor: poolPage === 1 ? "not-allowed" : "pointer", opacity: poolPage === 1 ? 0.4 : 1, color: "var(--foreground)", transition: "all 0.2s" }}
              >← Previous</button>

              {Array.from({ length: totalPoolPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPoolPages || Math.abs(p - poolPage) <= 2)
                .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                  if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push("…");
                  acc.push(p);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === "…" ? (
                    <span key={`ellipsis-${idx}`} style={{ fontSize: "12px", color: "hsl(var(--muted-foreground))", padding: "0 4px" }}>…</span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => { setPoolPage(item as number); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      style={{ width: "34px", height: "34px", borderRadius: "50%", border: poolPage === item ? `1.5px solid ${SAGE}` : "1.5px solid rgba(0,0,0,0.09)", background: poolPage === item ? "rgba(168,181,162,0.2)" : "rgba(255,255,255,0.7)", fontSize: "12px", fontWeight: poolPage === item ? 500 : 300, cursor: "pointer", color: poolPage === item ? "#3a4a37" : "var(--foreground)", transition: "all 0.18s" }}
                    >{item}</button>
                  )
                )
              }

              <button
                onClick={() => { setPoolPage((p) => Math.min(totalPoolPages, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                disabled={poolPage === totalPoolPages}
                style={{ padding: "8px 18px", borderRadius: "50px", border: "1.5px solid rgba(0,0,0,0.1)", background: "rgba(255,255,255,0.8)", fontSize: "12px", fontWeight: 300, cursor: poolPage === totalPoolPages ? "not-allowed" : "pointer", opacity: poolPage === totalPoolPages ? 0.4 : 1, color: "var(--foreground)", transition: "all 0.2s" }}
              >Next →</button>
            </div>
          )}
        </div>
      </section>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {donatePool && (
        <DonateModal pool={donatePool} onClose={() => setDonatePool(null)} />
      )}

      <SiteFooter />
    </div>
  );
}
